const STORAGE_KEY = "alphasearch:pinnedSavedSearchIds:v1";

/** Pin id for the Search page "All companies" default. */
export const ALL_COMPANIES_PIN_ID = "__all-companies__";

function normalizeId(id) {
  if (id == null) return null;
  const value = String(id).trim();
  return value || null;
}

function persistSinglePin(id) {
  const normalized = normalizeId(id);
  const next = normalized ? [normalized] : [];
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_error) {
    // ignore quota / private mode
  }
  return next;
}

/**
 * Resolve the pin id for a picker option (saved search or All companies).
 */
export function getSavedSearchPinId(option) {
  if (!option) return null;
  if (option.__allCompanies || option.id === ALL_COMPANIES_PIN_ID) {
    return ALL_COMPANIES_PIN_ID;
  }
  return normalizeId(option.id || option._id || option.workspace_id);
}

/**
 * Read the default pinned saved-search id list from localStorage.
 * Only one default is supported; legacy multi-pin values migrate to the first id.
 */
export function getPinnedSavedSearchIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const unique = [];
    const seen = new Set();
    parsed.forEach((id) => {
      const normalized = normalizeId(id);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      unique.push(normalized);
    });
    // Migrate older multi-pin prototypes → single default.
    if (unique.length > 1) {
      return persistSinglePin(unique[0]);
    }
    return unique;
  } catch (_error) {
    return [];
  }
}

/** Current default pin id, or null when none is set (Search falls back to All companies). */
export function getDefaultPinnedSavedSearchId(pinnedIds = getPinnedSavedSearchIds()) {
  return normalizeId(pinnedIds?.[0]) || null;
}

export function setPinnedSavedSearchIds(ids = []) {
  const first = normalizeId(Array.isArray(ids) ? ids[0] : ids);
  return persistSinglePin(first);
}

export function isSavedSearchPinned(id, pinnedIds = getPinnedSavedSearchIds()) {
  const normalized = normalizeId(id);
  if (!normalized) return false;
  return getDefaultPinnedSavedSearchId(pinnedIds) === normalized;
}

/**
 * Set exactly one default pin. Clicking the current default clears it
 * (Search falls back to All companies). Returns the next pinned-id list.
 */
export function togglePinnedSavedSearchId(id, pinnedIds = getPinnedSavedSearchIds()) {
  const normalized = normalizeId(id);
  if (!normalized) return pinnedIds;
  const current = getDefaultPinnedSavedSearchId(pinnedIds);
  if (current === normalized) {
    return persistSinglePin(null);
  }
  return persistSinglePin(normalized);
}

/**
 * Remove one id from the pin list (e.g. after delete). Returns the next list.
 */
export function removePinnedSavedSearchId(id, pinnedIds = getPinnedSavedSearchIds()) {
  const normalized = normalizeId(id);
  if (!normalized) return pinnedIds;
  if (getDefaultPinnedSavedSearchId(pinnedIds) !== normalized) return pinnedIds;
  return persistSinglePin(null);
}

/**
 * Order custom saved searches: the single pinned default first (when it is a
 * real saved search), then the rest in their original relative order.
 * Does not include "All companies".
 */
export function sortSavedSearchesByPin(items = [], pinnedIds = getPinnedSavedSearchIds()) {
  const list = Array.isArray(items) ? [...items] : [];
  if (!list.length) return list;

  const defaultId = getDefaultPinnedSavedSearchId(pinnedIds);
  if (!defaultId || defaultId === ALL_COMPANIES_PIN_ID) return list;

  const pinnedIndex = list.findIndex((item) => {
    const id = normalizeId(item?.id || item?._id || item?.workspace_id);
    return id === defaultId;
  });
  if (pinnedIndex <= 0) return list;

  const next = [...list];
  const [pinned] = next.splice(pinnedIndex, 1);
  return [pinned, ...next];
}

/** Discard this browser's default on explicit logout. */
export function clearPinnedSavedSearchIds() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    // Storage may be unavailable; do not prevent local logout.
  }
}
